import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { Spinner, Text } from '@revelio/ui';

import { RevelioWatermark } from '../../../../assets';
import { DashMetaRateLimitError } from '../../api/dash-meta-rate-limit-error';
import { getSharedPlot } from '../../api/shared-plots-api';
import { StaticChartRenderer } from './StaticChartRenderer';
import './shared-report.css';

import classNames from 'classnames';

import styles from './shared-plot-viewer.module.css';

export const SharedPlotViewer = () => {
  const { token } = useParams<{ token: string }>();

  const {
    data: snapshot,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['shared-plot', token],
    queryFn: () => getSharedPlot(token ?? ''),
    enabled: !!token,
    // Don't retry rate-limit errors — surface them immediately.
    retry: (failureCount, err) =>
      !(err instanceof DashMetaRateLimitError) && failureCount < 1,
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusContent}>
          <Spinner size="xl" />
          <Text className={styles.statusText}>Loading shared plot...</Text>
        </div>
      </div>
    );
  }

  if (error instanceof DashMetaRateLimitError) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusContent}>
          <Text
            as="h2"
            fontSize="bg"
            weight="bold"
            className={styles.statusTitle}
          >
            Too many requests
          </Text>
          <Text className={styles.statusMessage}>
            This shared plot is being viewed a lot right now. Please refresh in
            a minute.
          </Text>
        </div>
      </div>
    );
  }

  if (error || !snapshot?.component) {
    return (
      <div className={styles.statusPage}>
        <div className={styles.statusContent}>
          <Text
            as="h2"
            fontSize="bg"
            weight="bold"
            className={styles.statusTitle}
          >
            Plot not found
          </Text>
          <Text className={styles.statusMessage}>
            This shared plot link may be invalid or the plot may have been
            removed.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('shared-report-page', styles.page)}>
      {snapshot.title && (
        <Text
          as="h2"
          fontSize="md"
          weight="bold"
          lineClamp={1}
          tone="inherit"
          className={styles.plotTitle}
        >
          {snapshot.title}
        </Text>
      )}
      <div className={styles.chartArea}>
        <StaticChartRenderer
          chartDataEntry={{
            component: snapshot.component,
            props: snapshot.props,
            title: snapshot.title,
          }}
        />
      </div>
      <div className={styles.watermark}>
        <img
          className={styles.watermarkImage}
          src={RevelioWatermark}
          alt="Revelio Labs"
        />
      </div>
    </div>
  );
};
